import { useState } from "react";
import { useLanguage } from "@/hooks/useLanguage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, Copy, Check } from "lucide-react";

const mockConversion = (text: string): string => {
  if (!text) return '';
  
  return text.split('').map(char => {
    const natoMap: {[key: string]: string} = {
      'a': 'Alpha', 'b': 'Bravo', 'c': 'Charlie', 'd': 'Delta', 'e': 'Echo',
      'f': 'Foxtrot', 'g': 'Golf', 'h': 'Hotel', 'i': 'India', 'j': 'Juliet',
      'k': 'Kilo', 'l': 'Lima', 'm': 'Mike', 'n': 'November', 'o': 'Oscar',
      'p': 'Papa', 'q': 'Quebec', 'r': 'Romeo', 's': 'Sierra', 't': 'Tango',
      'u': 'Uniform', 'v': 'Victor', 'w': 'Whiskey', 'x': 'X-ray', 'y': 'Yankee',
      'z': 'Zulu', ' ': ' '
    };
    return natoMap[char.toLowerCase()] || char;
  }).join(' ');
};

const DemoSection = () => {
  const { t } = useLanguage();
  const [inputText, setInputText] = useState('');
  const [outputText, setOutputText] = useState('');
  const [copied, setCopied] = useState(false);

  const handleConvert = () => {
    const result = mockConversion(inputText);
    setOutputText(result);
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(outputText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <section id="demo" className="py-24 px-6 bg-muted/30">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-semibold mb-3">
            {t('demoTitle') || 'Try It Now'}
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            {t('demoSubtitle') || 'Experience the instant conversion without installing anything.'}
          </p>
        </div>

        <div className="bg-card border border-border rounded-lg p-6 md:p-8">
          <div className="space-y-6">
            <div>
              <Label htmlFor="input-text" className="text-sm font-medium mb-2 block">
                {t('demoInputLabel' as any) || 'Enter your text'}
              </Label>
              <Input
                id="input-text"
                className="h-12"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder={t('demoInputPlaceholder') as string || 'Type something...'}
                onKeyDown={(e) => e.key === 'Enter' && handleConvert()}
              />
            </div>
            
            <Button 
              className="w-full sm:w-auto gap-2" 
              onClick={handleConvert}
            >
              {t('demoTryIt') || 'Convert'}
              <ArrowRight className="h-4 w-4" />
            </Button>
            
            {outputText && (
              <div className="mt-6 p-4 bg-muted rounded-lg animate-fade-in">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-sm font-medium">{t('demoOutputLabel') || 'Result'}</Label>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleCopy}
                    className="gap-2 h-8"
                  >
                    {copied ? (
                      <>
                        <Check className="h-3.5 w-3.5" />
                        <span className="text-xs">{t('demoCopied' as any) || 'Copied'}</span>
                      </>
                    ) : (
                      <>
                        <Copy className="h-3.5 w-3.5" />
                        <span className="text-xs">{t('demoCopy' as any) || 'Copy'}</span>
                      </>
                    )}
                  </Button>
                </div>
                <div className="p-4 bg-background rounded-md text-sm leading-relaxed font-mono">
                  {outputText}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default DemoSection;
